import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';
import {
  BUDGET_TITLE,
  DESCRIPTION_BUTTON_TEXT,
  MODAL_TITLE,
  OmakasePlanPolicies,
  OmakasePlanStrategyOptions,
  POLICY_TITLE,
  STRATEGY_TITLE,
  STRATEGY_SUFFIX,
  POLICY_BUTTON_TEXT_RETURN,
  POLICY_BUTTON_TEXT_BALANCE,
} from 'shared-modules/constants/omakasePlan';
import {
  OmakasePlanStatusCheckType,
  useOmakasePlanInput,
  useOmakasePlanStatusCheck,
} from 'shared-modules/services/hooks/omakasePlanLogic';
import { OMAKASE_PLAN_NOT_FOUND } from 'shared-modules/constants/errorMesages';
import { OMAKASE_PLAN_DESCRIPTION_LINK } from 'shared-modules/config';
import { useAccountInfo } from 'shared-modules/hooks';
import { FX } from 'shared-modules/constants';
import { ExclusiveToggleButton, ExclusiveToggleButtonGroup } from '../../../../components/ExclusiveToggleButton';
import Detail from './Detail';
import TitleWithIcon from './TitleWithIcon';
import styles from './omakasePlanModal.module.scss';
import {
  closeOmakaseConfirmModal,
  closeOmakasePlanModal,
  openConfirmationModal,
  closeAutoSelectWarningInfo,
} from '../../../../redux/actions/modalActions';
import { Button, InputNumber } from '../../../../components';
import { CreateOmakasePlanIntro } from './CreateOmakasePlanIntro/CreateOmakasePlanIntro';
import budgetIcon from '../../../../assets/omakasePlan/budget-icon.png';
import termIcon from '../../../../assets/omakasePlan/term-icon.png';
import policyIcon from '../../../../assets/omakasePlan/policy-icon.png';
import descriptionIcon from '../../../../assets/omakasePlan/description-icon.png';
import policyReturnIcon from '../../../../assets/omakasePlan/policy-return-icon.png';
import policyBalanceIcon from '../../../../assets/omakasePlan/policy-balance-icon.png';

const OmakasePlanPolicyOptions = Object.freeze([
  { value: OmakasePlanPolicies.RETURN, policyText: POLICY_BUTTON_TEXT_RETURN, icon: policyReturnIcon, alt: 'return' },
  {
    value: OmakasePlanPolicies.BALANCE,
    policyText: POLICY_BUTTON_TEXT_BALANCE,
    icon: policyBalanceIcon,
    alt: 'balance',
  },
]);

export const OmakasePlanModal = memo(() => {
  const dispatch = useDispatch();
  const { isOpen: isOpenOmakasePlan } = useSelector((s) => s.modals.openOmakasePlanModal);
  const { budget, term, policy, createBtn, created, budgetMinError, hasError } = useOmakasePlanInput({
    isFocused: true,
  });
  const hasOmakasePlan = useOmakasePlanStatusCheck({ type: OmakasePlanStatusCheckType.SELECTED });
  const hasResult = useOmakasePlanStatusCheck({ type: OmakasePlanStatusCheckType.CREATING });
  const isCreatingLoading = useSelector((s) => s.omakasePlan.loading);
  const [waitingForCreate, setWaitingForCreate] = useState(false);

  const openOmakaseErrorModal = () =>
    openConfirmationModal({
      title: OMAKASE_PLAN_NOT_FOUND.title,
      bodyText: OMAKASE_PLAN_NOT_FOUND.body,
      buttonNextText: OMAKASE_PLAN_NOT_FOUND.buttonText,
      isOverlap: true,
    });

  useEffect(() => {
    if (!hasOmakasePlan) {
      dispatch(openOmakaseErrorModal());
    }
  }, [hasOmakasePlan, term, policy, dispatch]);

  useEffect(() => {
    budget.validation(budget.get);
  }, [term, policy, budget]);

  const handleClose = useCallback(() => {
    dispatch(closeAutoSelectWarningInfo());
    dispatch(closeOmakasePlanModal());
  }, [dispatch]);

  const handleDescription = () => window.open(OMAKASE_PLAN_DESCRIPTION_LINK);

  const [pressedCreateButton, setPressedCreateButton] = useState(false);
  const handleCreate = useCallback(() => {
    createBtn.onClick();
    setWaitingForCreate(true);
    setPressedCreateButton(true);
  }, [createBtn]);

  // ローディング完了後のみ、hasResultを判定する
  useEffect(() => {
    if (!waitingForCreate) return;
    if (isCreatingLoading) return;

    // 読み込み完了時、resultなければ、エラーモーダルを表示
    if (!hasResult) {
      dispatch(openOmakaseErrorModal());
    }

    // フラグをリセット
    setWaitingForCreate(false);
  }, [waitingForCreate, isCreatingLoading, hasResult, dispatch]);

  const accountInfo = useAccountInfo();
  const isFXLossCut = accountInfo[FX].isLossCut;
  const isFXMaintenance = useSelector((state) => state.settings.maintenanceInfo[FX].isMaintenance);
  useEffect(() => {
    if (!isOpenOmakasePlan) return;
    if (isFXLossCut || isFXMaintenance) {
      dispatch(closeAutoSelectWarningInfo());
      dispatch(closeOmakaseConfirmModal());
      dispatch(closeOmakasePlanModal());
    }
  }, [isOpenOmakasePlan, isFXLossCut, isFXMaintenance, dispatch]);

  return (
    <Modal
      show={isOpenOmakasePlan}
      onHide={handleClose}
      centered
      backdrop
      keyboard
      dialogClassName={styles.twoPaneDialog}
      contentClassName={styles.twoPaneContent}
    >
      <button type="button" className={styles.headerClose} aria-label="閉じる" onClick={handleClose}>
        <i className={classNames('material-icons', styles.closeIcon)}>clear</i>
      </button>

      <Modal.Body className={styles.flexBody}>
        <div className={styles.leftPane}>
          <div className={styles.title}>
            <div className={styles.titleText}>{MODAL_TITLE}</div>
            <button className={styles.description} type="button" onClick={handleDescription}>
              <img src={descriptionIcon} alt="descriptionIcon" />
              <div className={styles.descriptionText}>{DESCRIPTION_BUTTON_TEXT}</div>
            </button>
          </div>
          <hr className={styles.divider} />
          <div className={styles.selectArea}>
            <div className={styles.subContainer}>
              <TitleWithIcon iconImage={budgetIcon} title={BUDGET_TITLE} />
              <div className={styles.inputArea}>
                <InputNumber
                  value={budget.get}
                  name={budget.name}
                  className={styles.numberInput}
                  buttonStyle={styles.numberInputButton}
                  inputStyle={styles.inputStyle}
                  onChange={budget.set}
                  validateFunction={budget.validation}
                  max={9999}
                  errorMessages={budget.error}
                  withErrorTooltip
                  onlyIntegerAllowed
                  width={210}
                  step={5}
                  minByDecrement={5}
                />
                <div className={styles.inputUnitText}>万円</div>
              </div>
            </div>

            <div className={styles.subContainer}>
              <TitleWithIcon iconImage={termIcon} title={STRATEGY_TITLE} />
              <ExclusiveToggleButtonGroup className={styles.termButtonContainer} value={term.get} onChange={term.set}>
                {OmakasePlanStrategyOptions.map(({ value, name }) => (
                  <ExclusiveToggleButton key={value} value={value}>
                    <div className={styles.buttonContent}>
                      <div>{name}</div>
                      <div>{STRATEGY_SUFFIX}</div>
                    </div>
                  </ExclusiveToggleButton>
                ))}
              </ExclusiveToggleButtonGroup>
            </div>

            <div className={styles.subContainer}>
              <TitleWithIcon iconImage={policyIcon} title={POLICY_TITLE} />
              <ExclusiveToggleButtonGroup
                className={styles.policyButtonContainer}
                value={policy.get}
                onChange={policy.set}
              >
                {OmakasePlanPolicyOptions.map(({ value, policyText, icon, alt }) => (
                  <ExclusiveToggleButton key={value} value={value}>
                    <div className={styles.buttonContent}>
                      <div
                        className={classNames(styles.iconContainer, {
                          [styles.selected]: policy.get === value,
                        })}
                      >
                        <img src={icon} alt={alt} />
                      </div>
                      <div>{policyText}</div>
                    </div>
                  </ExclusiveToggleButton>
                ))}
              </ExclusiveToggleButtonGroup>
            </div>
          </div>

          <div className={styles.createButtonArea}>
            <div className={styles.errorPlaceholder}>
              {hasError && budgetMinError ? (
                <div className={styles.budgetInsufficientText}>
                  指定条件では、あと
                  <span className={styles.numberText}>{budgetMinError}</span>
                  <span className={styles.unitText}>万円</span>
                  の予算が必要です。
                </div>
              ) : null}
            </div>

            <Button
              green
              width={338}
              className={styles.createButton}
              disabled={createBtn.isDisabled}
              onClick={handleCreate}
            >
              {createBtn.label}
            </Button>
          </div>
        </div>
        <div className={classNames(styles.rightPane, { [styles.centered]: !created })}>
          {created ? <Detail /> : <CreateOmakasePlanIntro loading={pressedCreateButton} />}
        </div>
      </Modal.Body>
    </Modal>
  );
});
